/**
 * ListingDiffReviewModal.tsx
 *
 * Pre-Commit Visual Diff Reviewer and AAL3 Supervisor Step-Up Challenge Modal:
 * - Highlights additions (green), removals (red), modifications (yellow), and price shift percentages.
 * - Enforces supervisor step-up acknowledgment if policy thresholds are triggered.
 */

import React from 'react';
import type { Product, ProductVariant } from '../../plugins/commerce/commerce-contracts.js';
import type { ListingPatchValidationResult } from '../../plugins/commerce/governance/listing-governance.js';

export interface ListingDiffReviewModalProps {
  readonly isOpen: boolean;
  readonly originalProduct: Product;
  readonly proposedProduct: Partial<Product>;
  readonly originalVariants: readonly ProductVariant[];
  readonly proposedVariants: readonly { variantId: string; patch: Partial<ProductVariant> }[];
  readonly validationResult: ListingPatchValidationResult;
  readonly isSubmitting: boolean;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

export const ListingDiffReviewModal: React.FC<ListingDiffReviewModalProps> = ({
  isOpen,
  originalProduct,
  proposedProduct,
  originalVariants,
  proposedVariants,
  validationResult,
  isSubmitting,
  onConfirm,
  onCancel,
}) => {
  if (!isOpen) return null;

  const getTitle = (p: Partial<Product>) =>
    typeof p.title === 'string' ? p.title : Object.values(p.title || {})[0] || 'N/A';

  const origTitle = getTitle(originalProduct);
  const propTitle = getTitle(proposedProduct);
  const titleChanged = origTitle !== propTitle;

  const descChanged =
    JSON.stringify(originalProduct.description) !== JSON.stringify(proposedProduct.description);

  const statusChanged = proposedProduct.status && proposedProduct.status !== originalProduct.status;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        backdropFilter: 'blur(8px)',
        zIndex: 1100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
    >
      <div
        style={{
          backgroundColor: '#111827',
          border: '1px solid var(--theme-border, #1e293b)',
          borderRadius: '14px',
          width: '100%',
          maxWidth: '680px',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 48px rgba(0,0,0,0.8)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #1e293b', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 800, color: '#f8fafc' }}>
              🔍 Review Listing Changes Prior to Publishing
            </h3>
            <span style={{ fontSize: '11px', color: '#94a3b8' }}>
              Inspect field additions, modifications, and security gating before committing revision #{originalProduct.revision + 1}.
            </span>
          </div>
          <button
            onClick={onCancel}
            style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: '18px', cursor: 'pointer' }}
          >
            ✕
          </button>
        </div>

        {/* Violations Block (Blocks Publish) */}
        {!validationResult.allowed && (
          <div style={{ margin: '14px 20px 0', padding: '12px', borderRadius: '8px', backgroundColor: 'rgba(239, 68, 68, 0.15)', border: '1px solid #ef4444' }}>
            <div style={{ fontWeight: 700, fontSize: '12px', color: '#fca5a5', marginBottom: '4px' }}>
              🚫 Immutable Field Policy Violations:
            </div>
            {validationResult.violations.map((v, i) => (
              <div key={i} style={{ fontSize: '11px', color: '#fee2e2' }}>• {v}</div>
            ))}
          </div>
        )}

        {/* AAL3 MFA Trigger Warning */}
        {validationResult.requiresSupervisorMfa && (
          <div style={{ margin: '14px 20px 0', padding: '12px', borderRadius: '8px', backgroundColor: 'rgba(245, 158, 11, 0.15)', border: '1px solid #f59e0b' }}>
            <div style={{ fontWeight: 700, fontSize: '12px', color: '#fcd34d', marginBottom: '4px' }}>
              🛡️ ISO 29115 AAL3 Supervisor Step-Up Required:
            </div>
            {validationResult.mfaReasons.map((r, i) => (
              <div key={i} style={{ fontSize: '11px', color: '#fef3c7' }}>• {r}</div>
            ))}
          </div>
        )}

        {/* Diff Table */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #1e293b', color: '#94a3b8', fontSize: '11px', textTransform: 'uppercase' }}>
                <th style={{ padding: '8px', textAlign: 'left' }}>Field</th>
                <th style={{ padding: '8px', textAlign: 'left' }}>Live Value</th>
                <th style={{ padding: '8px', textAlign: 'left' }}>Proposed Revision</th>
              </tr>
            </thead>
            <tbody>
              {/* Title Diff */}
              {titleChanged && (
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', backgroundColor: 'rgba(56, 189, 248, 0.05)' }}>
                  <td style={{ padding: '8px', fontWeight: 700, color: '#38bdf8' }}>Title</td>
                  <td style={{ padding: '8px', color: '#ef4444', textDecoration: 'line-through' }}>{origTitle}</td>
                  <td style={{ padding: '8px', color: '#34d399', fontWeight: 700 }}>{propTitle}</td>
                </tr>
              )}

              {/* Status Diff */}
              {statusChanged && (
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', backgroundColor: 'rgba(56, 189, 248, 0.05)' }}>
                  <td style={{ padding: '8px', fontWeight: 700, color: '#38bdf8' }}>Status</td>
                  <td style={{ padding: '8px', color: '#ef4444' }}>{originalProduct.status}</td>
                  <td style={{ padding: '8px', color: '#34d399', fontWeight: 700 }}>{proposedProduct.status}</td>
                </tr>
              )}

              {/* Description Diff */}
              {descChanged && (
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', backgroundColor: 'rgba(56, 189, 248, 0.05)' }}>
                  <td style={{ padding: '8px', fontWeight: 700, color: '#38bdf8' }}>Description</td>
                  <td style={{ padding: '8px', color: '#94a3b8' }}>Updated content</td>
                  <td style={{ padding: '8px', color: '#34d399', fontWeight: 700 }}>Modified</td>
                </tr>
              )}

              {/* Variant Diffs */}
              {proposedVariants.map((vp) => {
                const orig = originalVariants.find((v) => v.variantId === vp.variantId);
                if (!orig) return null;
                const priceChanged = vp.patch.basePriceMinor && vp.patch.basePriceMinor !== orig.basePriceMinor;

                if (!priceChanged) return null;

                const oldCents = parseInt(orig.basePriceMinor, 10);
                const newCents = parseInt(vp.patch.basePriceMinor || '0', 10);
                const pct = oldCents > 0 ? (((newCents - oldCents) / oldCents) * 100).toFixed(1) : '0';

                return (
                  <tr key={vp.variantId} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', backgroundColor: 'rgba(245, 158, 11, 0.08)' }}>
                    <td style={{ padding: '8px', fontWeight: 700, color: '#fbbf24' }}>Price ({orig.sku})</td>
                    <td style={{ padding: '8px', color: '#ef4444', textDecoration: 'line-through' }}>
                      ${(oldCents / 100).toFixed(2)}
                    </td>
                    <td style={{ padding: '8px', color: '#34d399', fontWeight: 700 }}>
                      ${(newCents / 100).toFixed(2)}{' '}
                      <span style={{ fontSize: '10px', color: Number(pct) < 0 ? '#fb7185' : '#34d399' }}>
                        ({Number(pct) > 0 ? `+${pct}` : pct}%)
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Footer Actions */}
        <div style={{ padding: '14px 20px', borderTop: '1px solid #1e293b', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button
            type="button"
            onClick={onCancel}
            style={{ padding: '8px 14px', borderRadius: '6px', backgroundColor: 'transparent', border: '1px solid #334155', color: '#cbd5e1', cursor: 'pointer' }}
          >
            ← Back to Editing
          </button>
          <button
            type="button"
            disabled={!validationResult.allowed || isSubmitting}
            onClick={onConfirm}
            style={{
              padding: '8px 18px',
              borderRadius: '6px',
              backgroundColor: validationResult.allowed ? '#34d399' : '#475569',
              color: '#000',
              fontWeight: 800,
              fontSize: '12px',
              border: 'none',
              cursor: validationResult.allowed ? 'pointer' : 'not-allowed',
            }}
          >
            {isSubmitting ? 'Committing...' : validationResult.requiresSupervisorMfa ? '🔑 Authenticate & Publish' : '✓ Confirm & Publish'}
          </button>
        </div>
      </div>
    </div>
  );
};
